import React from 'react';
import { motion } from 'framer-motion';
import { Edit2, Copy, Trash2, PlusCircle, CheckCircle, User } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';

export function PersonaLibraryCard({ persona, onEdit, onDuplicate, onDelete, onAddToPanel, isInPanel }) {
  const { t } = useTranslation();

  const initials = persona.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '??';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-stone-200 p-5 relative group hover:shadow-md transition-shadow cursor-pointer"
      onClick={onEdit}
    >
      {/* Action buttons */}
      <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
        <button onClick={onAddToPanel} className={`p-1.5 rounded ${isInPanel ? 'text-green-600 bg-green-50' : 'text-stone-400 hover:text-amber-600 hover:bg-amber-50'}`} title={isInPanel ? t('personaLib.inPanel') : t('personaLib.addToPanel')}>
          {isInPanel ? <CheckCircle className="w-4 h-4" /> : <PlusCircle className="w-4 h-4" />}
        </button>
        <button onClick={onDuplicate} className="p-1.5 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded" title={t('personaLib.duplicate')}>
          <Copy className="w-4 h-4" />
        </button>
        <button onClick={onDelete} className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded" title={t('personaLib.delete')}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Avatar + name */}
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white font-medium text-sm flex-shrink-0">
          {initials}
        </div>
        <div className="min-w-0">
          <h3 className="font-medium text-stone-900 truncate">{persona.name}</h3>
          <p className="text-sm text-stone-500">{persona.age} {t('persona.years')}{persona.gender ? `, ${persona.gender}` : ''}</p>
        </div>
      </div>

      {/* Key info */}
      <div className="space-y-1 text-sm text-stone-600 mb-3">
        {persona.region && <div className="truncate">📍 {persona.region}</div>}
        {persona.employment && <div className="truncate">💼 {persona.employment}</div>}
        {persona.archetype && (
          <div className="mt-2">
            <span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">{persona.archetype}</span>
          </div>
        )}
      </div>

      {/* Tags */}
      {persona.tags && persona.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {persona.tags.slice(0, 3).map(tag => (
            <span key={tag} className="px-2 py-0.5 bg-stone-100 text-stone-600 text-xs rounded">{tag}</span>
          ))}
          {persona.tags.length > 3 && (
            <span className="text-xs text-stone-400">+{persona.tags.length - 3}</span>
          )}
        </div>
      )}

      {/* Usage count */}
      {persona.usage_count > 0 && (
        <div className="mt-2 text-xs text-stone-400">
          {t('personaLib.usedTimes', { count: persona.usage_count })}
        </div>
      )}
    </motion.div>
  );
}
